import { LayerData, tLayerSettings, userViewerSettingsAtom } from '@/atoms';
import { colours } from '@/core';
import { PositionGroup2 } from '@/core/iprj';
import { getResourcesEndpoint } from '@/core/resources';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { toOfflineUrl, useServeOffline } from '@/lib/offline/offlineResourceBase';
import { useFrame, useLoader, useThree } from '@react-three/fiber';
import { useAtomValue } from 'jotai';
import { Suspense, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import * as THREE from 'three';
import { Loader } from './Loader';
import { MinimalHotspot } from './MinimalHotspot';

import logoURL from '@/assets/logo.png';

/**
 * Debug-only rotation correction for equirect panoramas. Lives on `window` so
 * we can A/B test 90°/180° axis swaps from DevTools without a rebuild while
 * we sort out new scanner orientations (OmniSLAM, etc.).
 *
 *   __panoDebug.set('z', 90)   // absolute angle in degrees
 *   __panoDebug.add('y', 90)   // additive, wraps at 360
 *   __panoDebug.fov = 85       // pinhole-only: horizontal FOV override (deg)
 *   __panoDebug.reset()
 *   __panoDebug.log = true     // dump incoming quaternions on next render
 *
 * Order applied as a post-multiply on the entry quaternion: X then Y then Z.
 */
const __panoDebug = {
  x: 0,
  y: 0,
  z: 0,
  fov: 0,
  log: false,
  set(axis: 'x' | 'y' | 'z', deg: number) {
    (this as Record<'x' | 'y' | 'z', number>)[axis] = deg;
    console.log('[IPRJ2 debug] rot=', this.x, this.y, this.z);
  },
  add(axis: 'x' | 'y' | 'z', deg: number) {
    const k = axis as 'x' | 'y' | 'z';
    (this as Record<'x' | 'y' | 'z', number>)[k] = ((this as Record<'x' | 'y' | 'z', number>)[k] + deg) % 360;
    console.log('[IPRJ2 debug] rot=', this.x, this.y, this.z);
  },
  reset() {
    this.x = 0;
    this.y = 0;
    this.z = 0;
    this.fov = 0;
    console.log('[IPRJ2 debug] reset');
  },
};
if (typeof window !== 'undefined') {
  (window as unknown as { __panoDebug: typeof __panoDebug }).__panoDebug = __panoDebug;
}

function buildDebugQuaternion(): THREE.Quaternion {
  const q = new THREE.Quaternion();
  if (__panoDebug.x) {
    q.multiply(
      new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), THREE.MathUtils.degToRad(__panoDebug.x)),
    );
  }
  if (__panoDebug.y) {
    q.multiply(
      new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), THREE.MathUtils.degToRad(__panoDebug.y)),
    );
  }
  if (__panoDebug.z) {
    q.multiply(
      new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), THREE.MathUtils.degToRad(__panoDebug.z)),
    );
  }
  return q;
}

type ImageForPick = {
  hasBlurredVariants?: boolean | null;
  blurredFilename?: string | null;
  smallBlurredFilename?: string | null;
  largeBlurredFilename?: string | null;
  hasCompressedVariants?: boolean | null;
  smallFilename?: string | null;
  largeFilename?: string | null;
  filename: string;
};

const pickImageFile = (image: ImageForPick, res: 'small' | 'large'): string => {
  if (image.hasBlurredVariants) {
    if (res === 'small' && image.smallBlurredFilename) return image.smallBlurredFilename;
    if (res === 'large' && image.largeBlurredFilename) return image.largeBlurredFilename;
    return image.blurredFilename ?? image.filename;
  }
  if (res === 'small' && image.hasCompressedVariants && image.smallFilename) return image.smallFilename;
  if (res === 'large' && image.hasCompressedVariants && image.largeFilename) return image.largeFilename;
  return image.largeFilename || image.filename;
};

const imageFilterVertexShader = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

// Equirectangular textures need U flipped (repeat.x = -1, offset.x = 1)
// which built-in materials handle automatically but ShaderMaterial does not.
const equirectVertexShader = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = vec2(1.0 - uv.x, uv.y);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const imageFilterFragmentShader = /* glsl */ `
uniform sampler2D map;
uniform float brightness;
uniform float contrast;
uniform float saturation;
uniform float opacity;
varying vec2 vUv;

// Linear → sRGB (matches Three.js LinearTosRGB)
vec3 linearToSrgb(vec3 c) {
  vec3 lo = c * 12.92;
  vec3 hi = 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055;
  return mix(lo, hi, step(0.0031308, c));
}

void main() {
  vec4 texColor = texture2D(map, vUv);
  vec3 color = texColor.rgb;

  // Brightness (additive)
  color += brightness;

  // Contrast (scale around midpoint 0.5)
  color = (color - 0.5) * contrast + 0.5;

  // Saturation (mix with luminance)
  float lum = dot(color, vec3(0.2126, 0.7152, 0.0722));
  color = mix(vec3(lum), color, saturation);

  gl_FragColor = vec4(linearToSrgb(clamp(color, 0.0, 1.0)), texColor.a * opacity);
}
`;

function createImageFilterMaterial(
  texture: THREE.Texture,
  side: THREE.Side,
  settings: tLayerSettings,
): THREE.ShaderMaterial {
  const opacity = settings.imageOpacity ?? 1.0;
  return new THREE.ShaderMaterial({
    uniforms: {
      map: { value: texture },
      brightness: { value: (settings.imageBrightness ?? 0) / 100 },
      contrast: { value: 1 + (settings.imageContrast ?? 0) / 100 },
      saturation: { value: 1 + (settings.imageSaturation ?? 0) / 100 },
      opacity: { value: opacity },
    },
    vertexShader: imageFilterVertexShader,
    fragmentShader: imageFilterFragmentShader,
    side,
    depthWrite: false,
    depthTest: false,
    toneMapped: false,
    fog: false,
    transparent: opacity < 1,
  });
}

/**
 * Equirectangular panorama entry.
 *
 * Uses a stable ShaderMaterial held in a ref so that filter-slider updates
 * only poke uniforms instead of swapping materials / toggling children,
 * which was causing R3F to re-reconcile the mesh and rotate the sphere.
 */
function EquirectEntry({
  entry,
  texture,
  sphereGeom,
  settings,
}: {
  entry: PositionGroup2['entries'][number];
  texture: THREE.Texture;
  sphereGeom: THREE.SphereGeometry;
  settings: tLayerSettings | undefined;
}) {
  const matRef = useRef<THREE.ShaderMaterial | null>(null);

  // Create the material once
  if (!matRef.current) {
    matRef.current = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: texture },
        brightness: { value: 0 },
        contrast: { value: 1 },
        saturation: { value: 1 },
        opacity: { value: 1 },
      },
      vertexShader: equirectVertexShader,
      fragmentShader: imageFilterFragmentShader,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
      toneMapped: false,
      fog: false,
    });
  }

  // Update uniforms when texture or settings change — no new material, no reconciliation
  useEffect(() => {
    const mat = matRef.current!;
    mat.uniforms.map.value = texture;
    mat.uniforms.brightness.value = (settings?.imageBrightness ?? 0) / 100;
    mat.uniforms.contrast.value = 1 + (settings?.imageContrast ?? 0) / 100;
    mat.uniforms.saturation.value = 1 + (settings?.imageSaturation ?? 0) / 100;
    const opacity = settings?.imageOpacity ?? 1.0;
    mat.uniforms.opacity.value = opacity;
    mat.transparent = opacity < 1;
    mat.needsUpdate = true;
  }, [
    texture,
    settings?.imageBrightness,
    settings?.imageContrast,
    settings?.imageSaturation,
    settings?.imageOpacity,
  ]);

  const q = useMemo(
    () =>
      new THREE.Quaternion(
        entry.rotationQuaternion[0],
        entry.rotationQuaternion[1],
        entry.rotationQuaternion[2],
        entry.rotationQuaternion[3],
      ),
    [
      entry.rotationQuaternion[0],
      entry.rotationQuaternion[1],
      entry.rotationQuaternion[2],
      entry.rotationQuaternion[3],
    ],
  );

  useEffect(() => {
    if (__panoDebug.log) {
      console.log('[IPRJ2 debug] entry quaternion', {
        id: (entry as unknown as { id?: string }).id,
        q: [
          entry.rotationQuaternion[0],
          entry.rotationQuaternion[1],
          entry.rotationQuaternion[2],
          entry.rotationQuaternion[3],
        ],
      });
    }
  }, [entry, q]);

  // Compose the live debug correction onto the entry quaternion every frame
  // so DevTools tweaks (`__panoDebug.add('z', 90)`) take effect without a
  // remount. Post-multiply = rotation in the image's local frame.
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame(() => {
    if (!meshRef.current) return;
    if (__panoDebug.x === 0 && __panoDebug.y === 0 && __panoDebug.z === 0) {
      meshRef.current.quaternion.copy(q);
      return;
    }
    meshRef.current.quaternion.copy(q).multiply(buildDebugQuaternion());
  });

  return (
    <mesh ref={meshRef} quaternion={q} renderOrder={-9999} material={matRef.current}>
      <primitive object={sphereGeom} />
    </mesh>
  );
}

/**
 * Pinhole / fisheye entry. Mirrors EquirectEntry's debug-rotation pattern:
 * holds the wrapper group in a ref and post-multiplies `__panoDebug` onto it
 * every frame, so DevTools tweaks update the orientation live without a
 * remount.
 *
 * Debug semantics match the pano: the rotation is applied in the camera's
 * local frame around the camera position. The wrapper group sits at the
 * parent group's origin (= camera world position), and its quaternion is set
 * to `q * debug * q⁻¹` — the world-frame conjugate of a local-frame post-
 * multiply, so the inner mesh's effective world orientation becomes `q *
 * debug` (matching `meshRef.quaternion.copy(q).multiply(debug)` in the pano
 * branch) and the offset position rotates around the camera origin.
 */
function PinholeEntry({
  entry,
  texture,
  hasFilters,
  settings,
}: {
  entry: PositionGroup2['entries'][number];
  texture: THREE.Texture;
  hasFilters: boolean;
  settings: tLayerSettings | undefined;
}) {
  const k = 100;

  const q = useMemo(
    () =>
      new THREE.Quaternion(
        entry.rotationQuaternion[0],
        entry.rotationQuaternion[1],
        entry.rotationQuaternion[2],
        entry.rotationQuaternion[3],
      ),
    [
      entry.rotationQuaternion[0],
      entry.rotationQuaternion[1],
      entry.rotationQuaternion[2],
      entry.rotationQuaternion[3],
    ],
  );

  const Wpx = entry.width;
  const Hpx = entry.height;
  const sx = entry.pixelWidth; // meters per pixel
  const sy = entry.pixelHeight; // meters per pixel
  const fx = entry.focalLengthX!; // meters
  const fy = entry.focalLengthY! || entry.focalLengthX!; // meters
  const cx = entry.principalPointX ?? Wpx / 2;
  const cy = entry.principalPointY ?? Hpx / 2;

  const Sx = Wpx * sx * k;
  const Sy = Hpx * sy * k;

  const bleed = 1.002;

  let d: number;
  let planeWidth: number;
  let planeHeight: number;
  let tx: number;
  let ty: number;
  let dir: THREE.Vector3;
  let side: THREE.Side;

  if (sx && sy) {
    d = ((fx + fy) / 2) * k;
    planeWidth = (d / (fx * k)) * Sx * bleed;
    planeHeight = (d / (fy * k)) * Sy * bleed;
    tx = (cx - Wpx / 2) * sx * k;
    ty = (cy - Hpx / 2) * sy * k;
    dir = new THREE.Vector3(0, 0, -1).applyQuaternion(q).normalize();
    side = THREE.FrontSide;
  } else {
    planeWidth = Wpx * bleed;
    planeHeight = Hpx * bleed;
    tx = cx - Wpx / 2;
    ty = cy - Hpx / 2;
    dir = new THREE.Vector3(0, 0, 1).applyQuaternion(q).normalize();
    side = THREE.BackSide;
    d = (entry.width + entry.height) / 4;
  }

  const offset = dir.clone().multiplyScalar(d);
  const translation = new THREE.Vector3(tx, ty, 0).applyQuaternion(q);

  const needsUvFlip = !sx && !sy;
  const planeGeom = useMemo(() => {
    const geom = new THREE.PlaneGeometry(planeWidth, planeHeight);
    if (needsUvFlip) {
      const uvAttr = geom.attributes.uv;
      for (let vi = 0; vi < uvAttr.count; vi++) {
        uvAttr.setY(vi, 1.0 - uvAttr.getY(vi));
      }
    }
    return geom;
  }, [planeWidth, planeHeight, needsUvFlip]);

  const pinholeMaterial =
    hasFilters && settings ? createImageFilterMaterial(texture, side, settings) : undefined;

  // Wrapper group rotation absorbs __panoDebug. When debug is zero, identity.
  // When non-zero, set wrapper.q = q * debug * q⁻¹ so the inner mesh's world
  // orientation becomes q * debug (local-frame post-multiply, same as pano)
  // and the offset rotates around the camera origin.
  const wrapperRef = useRef<THREE.Group>(null);
  const planeMeshRef = useRef<THREE.Mesh>(null);
  useFrame(() => {
    // FOV override: plane size is fixed, so a different FOV is purely a
    // different viewing distance along the camera direction.
    if (planeMeshRef.current) {
      if (__panoDebug.fov > 0) {
        const dFov = planeWidth / bleed / 2 / Math.tan(THREE.MathUtils.degToRad(__panoDebug.fov) / 2);
        planeMeshRef.current.position.copy(dir).multiplyScalar(dFov);
      } else {
        planeMeshRef.current.position.copy(offset);
      }
    }
    if (!wrapperRef.current) return;
    if (__panoDebug.x === 0 && __panoDebug.y === 0 && __panoDebug.z === 0) {
      wrapperRef.current.quaternion.identity();
      return;
    }
    const debug = buildDebugQuaternion();
    const conj = q.clone().multiply(debug).multiply(q.clone().invert());
    wrapperRef.current.quaternion.copy(conj);
  });

  return (
    <group ref={wrapperRef}>
      <mesh ref={planeMeshRef} position={offset.toArray()} quaternion={q}>
        <group position={translation}>
          <mesh renderOrder={-9999} material={pinholeMaterial || undefined} geometry={planeGeom}>
            {!pinholeMaterial && (
              <meshBasicMaterial
                map={texture}
                side={side}
                depthWrite={false}
                depthTest={false}
                toneMapped={false}
                fog={false}
              />
            )}
          </mesh>
        </group>
      </mesh>
    </group>
  );
}

/**
 * Creates a repeated-logo fallback texture for equirectangular use.
 * Best used on a sphere or other large surface where you want the logo
 * repeated all around.
 */
export async function createLogoGridTexture(
  logoUrl: string,
  options?: {
    size?: number; // canvas size
    tileSize?: number; // size of each repeated tile
    logoScale?: number; // how large the logo is inside each tile
    background?: string; // optional background fill
    opacity?: number; // logo opacity
  },
): Promise<THREE.Texture> {
  const {
    size = 1024,
    tileSize = 256,
    logoScale = 0.3,
    background = 'rgba(255,255,255,0.0)',
    opacity = 1,
  } = options || {};

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);

  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, size, size);
  }

  const img = await loadImage(logoUrl);

  const drawSize = tileSize * logoScale;

  for (let y = 0; y < size; y += tileSize) {
    for (let x = 0; x < size; x += tileSize) {
      const dx = x + (tileSize - drawSize) / 2;
      const dy = y + (tileSize - drawSize) / 2;

      ctx.save();
      ctx.globalAlpha = opacity;

      // Optional: alternate slight rotation for a nicer pattern
      const cx = x + tileSize / 2;
      const cy = y + tileSize / 2;
      ctx.translate(cx, cy);
      ctx.rotate((((x / tileSize + y / tileSize) % 2 === 0 ? 0 : 15) * Math.PI) / 180);
      ctx.drawImage(img, -drawSize / 2, -drawSize / 2, drawSize, drawSize);
      ctx.restore();
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.needsUpdate = true;

  return texture;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * GPU max texture size, set from `gl.capabilities.maxTextureSize` at mount time
 * (see IPRJ component below). We use this to pre-resize oversized images during
 * bitmap creation so they never hit Three.js's canvas-based resize path, which
 * interacts badly with `imageOrientation: 'flipY'` for certain JPEG encodings
 * (e.g. some Trimble scanner outputs render top/bottom-flipped after the
 * canvas resize step).
 *
 * Default is a conservative 16000 — safely under the 16384 limit of virtually
 * every modern GPU. Overridden at runtime once the renderer is available.
 */
let maxTextureSize = 16000;

export function setMaxTextureSize(size: number) {
  // Clamp to something sane — some mobile GPUs report huge values.
  if (size > 0) maxTextureSize = Math.min(size, 16383);
}

/**
 * Parse a JPEG's SOFn marker to read dimensions without decoding pixels.
 * Returns null for non-JPEG blobs or malformed headers; the caller falls
 * back to a non-resized decode in that case.
 */
async function readJpegDimensionsFromBlob(blob: Blob): Promise<{ width: number; height: number } | null> {
  const head = new Uint8Array(await blob.slice(0, 256 * 1024).arrayBuffer());
  if (head.length < 4 || head[0] !== 0xff || head[1] !== 0xd8) return null;

  let i = 2;
  while (i < head.length - 9) {
    if (head[i] !== 0xff) {
      i++;
      continue;
    }
    // Skip fill bytes
    while (i < head.length && head[i] === 0xff) i++;
    if (i >= head.length) break;
    const marker = head[i++];

    // Standalone markers with no length field
    if (marker === 0xd8 || marker === 0xd9 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;

    // SOFn markers (excluding DHT 0xC4, JPG 0xC8, DAC 0xCC)
    const isSof =
      (marker >= 0xc0 && marker <= 0xc3) ||
      (marker >= 0xc5 && marker <= 0xc7) ||
      (marker >= 0xc9 && marker <= 0xcb) ||
      (marker >= 0xcd && marker <= 0xcf);

    if (i + 2 > head.length) break;
    const segLen = (head[i] << 8) | head[i + 1];

    if (isSof) {
      if (i + 7 > head.length) break;
      const height = (head[i + 3] << 8) | head[i + 4];
      const width = (head[i + 5] << 8) | head[i + 6];
      return { width, height };
    }

    i += segLen;
  }

  return null;
}

/**
 * Custom texture loader that uses fetch with credentials.
 * On failure, resolves with an opaque alpha-grid texture instead of throwing.
 *
 * Atomically decodes + flips + resizes oversized JPEGs via `createImageBitmap`,
 * bypassing Three.js's own oversize-resize path. See `maxTextureSize` comment
 * above for why.
 */
class CredentialsTextureLoader extends THREE.Loader<THREE.Texture> {
  load(
    url: string,
    onLoad?: (texture: THREE.Texture) => void,
    _onProgress?: (event: ProgressEvent) => void,
    _onError?: (err: unknown) => void,
  ): THREE.Texture {
    const texture = new THREE.Texture();

    // Offline (`offlineres://`) URLs are served by the Tauri handler with
    // `Access-Control-Allow-Origin: *`, which forbids credentialed requests
    // (CORS blocks them). Only attach credentials for the network endpoint.
    const offline = url.startsWith('offlineres://');

    fetch(url, offline ? {} : { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then(async (blob) => {
        const dims = blob.type === 'image/jpeg' ? await readJpegDimensionsFromBlob(blob) : null;

        // No imageOrientation:'flipY' — pixels stay in native top-left order.
        // Equirect rendering flips V in its vertex shader; pinhole IPRJ images
        // need top-left origin to match their rotation matrices.
        const bitmapOptions: ImageBitmapOptions = { imageOrientation: 'flipY' };
        if (dims) {
          const scale = Math.min(1, maxTextureSize / Math.max(dims.width, dims.height));
          if (scale < 1) {
            bitmapOptions.resizeWidth = Math.floor(dims.width * scale);
            bitmapOptions.resizeHeight = Math.floor(dims.height * scale);
            bitmapOptions.resizeQuality = 'high';
          }
        }

        return createImageBitmap(blob, bitmapOptions);
      })
      .then((bitmap) => {
        texture.image = bitmap;
        texture.needsUpdate = true;
        onLoad?.(texture);
      })
      .catch((err) => {
        console.warn(`[IPRJ2] Failed to load texture: ${url}`, err);
        createLogoGridTexture(logoURL).then((fallback) => {
          texture.image = fallback.image;
          texture.needsUpdate = true;
          onLoad?.(texture);
        });
      });

    return texture;
  }
}

export const IPRJ = ({
  setPosition,
  // images,
  imagePositions: positions,
  layers,
  imageId,
  setImageId,
  hoverImageId,
  setHoverImageId,
  position,
}: {
  setPosition: Dispatch<SetStateAction<THREE.Vector3>>;
  visible?: boolean;
  images: Omit<Image, 'layer'>[] | null;
  imagePositions:
    | (PositionGroup2 & { cameraPosition: THREE.Vector3; renderPosition: THREE.Vector3; display: boolean })[]
    | null;
  layers: LayerData[] | null;
  position: THREE.Vector3;
  imageId: null | string;
  setImageId: Dispatch<SetStateAction<string | null>>;
  hoverImageId: string | null;
  setHoverImageId: Dispatch<SetStateAction<string | null>>;
}) => {
  const gl = useThree((state) => state.gl);
  const canvas = gl.domElement;
  const layerSettings = useLayerSettingsMany(layers?.map((l) => l.id) || []);
  const { projectId } = useParams<{ projectId: string }>();
  const serveOffline = useServeOffline(projectId);

  // Sync the loader's pre-resize ceiling to the GPU's actual max texture size
  // so oversized pano JPEGs get baked-in resize during createImageBitmap and
  // never hit Three.js's canvas-based resize path. useMemo runs during render,
  // which is before IPRJSkybox (child) fires useLoader.
  useMemo(() => setMaxTextureSize(gl.capabilities.maxTextureSize), [gl]);

  const viewerSettings = useAtomValue(userViewerSettingsAtom);
  const renderImagePOIs = viewerSettings?.showImagePOIs ?? true;

  const imagePosition = positions?.find((p) => p.entries[0].id === imageId);

  useEffect(() => {
    if (!imageId || !imagePosition) return;
    if (
      imagePosition.cameraPosition.x === position.x &&
      imagePosition.cameraPosition.y === position.y &&
      imagePosition.cameraPosition.z === position.z
    )
      return;

    setPosition(imagePosition.cameraPosition.clone());
  }, [imageId, positions, setPosition]);

  // Prefetch textures on hover so clicking feels instant
  useEffect(() => {
    if (!hoverImageId || !positions || !layers) return;
    if (hoverImageId === imageId) return;

    const hoveredPosition = positions.find((p) => p.entries[0].id === hoverImageId);
    if (!hoveredPosition) return;

    const layer = layers.find((l) => l.id === hoveredPosition.entries[0].layerId);
    console.log('layer', layer);
    if (!layer) return;

    // Low-res URLs (same logic as IPRJSkybox's initialUrls)
    const lowResUrls = hoveredPosition.entries.map((image) => {
      const filename = pickImageFile(image, 'small');
      const httpsUrl = `${getResourcesEndpoint()}/${layer.root}/${encodeURIComponent(filename)}`;
      return serveOffline ? toOfflineUrl(httpsUrl) : httpsUrl;
    });

    // Warm r3f's useLoader cache for low-res textures
    useLoader.preload(CredentialsTextureLoader, lowResUrls);

    // Warm HTTP cache for high-res textures
    hoveredPosition.entries.forEach((image) => {
      if (
        (image.hasCompressedVariants && image.smallFilename) ||
        (image.hasBlurredVariants && image.smallBlurredFilename)
      ) {
        const filename = pickImageFile(image, 'large');
        const httpsUrl = `${getResourcesEndpoint()}/${layer.root}/${encodeURIComponent(filename)}`;
        const url = serveOffline ? toOfflineUrl(httpsUrl) : httpsUrl;
        fetch(url, serveOffline ? {} : { credentials: 'include' }).catch(() => {});
      }
    });
  }, [hoverImageId, imageId, positions, layers, serveOffline]);

  if (!positions || positions.length === 0 || !imageId) return null;

  return (
    <>
      <Suspense fallback={<Loader />}>
        {imagePosition ? (
          <IPRJSkybox
            position={imagePosition}
            layers={layers}
            setPosition={setPosition}
            layerSettings={layerSettings}
          />
        ) : null}
      </Suspense>
      <group>
        {/* optional: global postprocessing bloom */}
        {/* <EffectComposer multisampling={4}>
          <Bloom
            intensity={0.7}
            luminanceThreshold={0.2}
            luminanceSmoothing={0.9}
            blendFunction={BlendFunction.SCREEN}
          />
        </EffectComposer> */}

        {renderImagePOIs
          ? positions
              .filter((p) => p.display)
              .map((p, i) => (
                <MinimalHotspot
                  key={i}
                  position={p.renderPosition}
                  active={imageId === p.entries[0].id}
                  hovered={hoverImageId === p.entries[0].id}
                  colorDefault={
                    layerSettings?.[p.entries[0].layerId as string]?.imagePointColor || colours.images.default
                  }
                  colorHover={colours.images.hover}
                  colorActive={colours.images.active}
                  onPointerEnter={() => {
                    if (imageId === p.entries[0].id) return;
                    setHoverImageId(p.entries[0].id);
                    canvas.style.cursor = 'pointer';
                  }}
                  onPointerLeave={() => {
                    setHoverImageId(null);
                    canvas.style.cursor = 'auto';
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setImageId(p.entries[0].id);
                  }}
                />
              ))
          : null}
      </group>
    </>
  );
};

function applyEquirectangularTextureSettings(texture: THREE.Texture, maxAnisotropy: number): void {
  texture.wrapS = THREE.RepeatWrapping;
  texture.repeat.x = -1;
  texture.offset.x = 1;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.anisotropy = Math.min(8, maxAnisotropy);
}

function getDirectionVector(headingDeg: number, rollDeg: number, pitchDeg: number): number[] {
  // Convert to radians (negate heading and roll for X,Y)
  const h = (-headingDeg * Math.PI) / 180;
  const r = (-rollDeg * Math.PI) / 180;
  const p = (pitchDeg * Math.PI) / 180;

  const ch = Math.cos(h),
    sh = Math.sin(h);
  const cp = Math.cos(p),
    sp = Math.sin(p);
  const cr = Math.cos(r),
    sr = Math.sin(r);

  // X and Y from HRP rotation of [0,0,1]
  const x = ch * sp + sh * sr * cp;
  const y = sh * sp - ch * sr * cp;

  // Z uses original (unnegated) roll
  const r_original = (rollDeg * Math.PI) / 180;
  const z = Math.sin(p) * Math.cos(r_original);

  return [x, y, z];
}

function quatFromHRP(hrp: [number, number, number], rotationOrder: 1 | 2 | 3 | 4 | 6): THREE.Quaternion {
  const [hDeg, rDeg, pDeg] = hrp;
  const h = THREE.MathUtils.degToRad(hDeg);
  const r = THREE.MathUtils.degToRad(rDeg);
  const p = THREE.MathUtils.degToRad(pDeg);

  // per spec: Heading = rot around Z, Roll = rot around X, Pitch = rot around Y
  const H = new THREE.Matrix4().makeRotationZ(h);
  const R = new THREE.Matrix4().makeRotationX(r);
  const P = new THREE.Matrix4().makeRotationY(p);

  // multiply in the exact order from the .iprj file (left→right)
  // 1 – Heading * Pitch * Roll
  // 2 – Heading * Roll * Pitch
  // 3 – Roll * Pitch * Heading
  // 4 – Pitch * Roll * Heading
  const m = new THREE.Matrix4().identity();
  switch (rotationOrder) {
    case 1:
      m.multiply(H).multiply(P).multiply(R);
      break;
    case 2:
      m.multiply(H).multiply(R).multiply(P);
      break;
    case 3:
      m.multiply(R).multiply(P).multiply(H);
      break;
    case 4:
      m.multiply(P).multiply(R).multiply(H);
      break;
    default:
      throw new Error('Invalid rotation order: ' + rotationOrder);
  }

  return new THREE.Quaternion().setFromRotationMatrix(m);
}

const IPRJSkybox = ({
  position,
  layers,
  layerSettings,
}: {
  position: PositionGroup2;
  layers: LayerData[] | null;
  setPosition: Dispatch<SetStateAction<THREE.Vector3>>;
  layerSettings: Record<string, tLayerSettings>;
}) => {
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);
  const { projectId } = useParams<{ projectId: string }>();
  const serveOffline = useServeOffline(projectId);

  const layer = useMemo(() => {
    if (!layers) return null;
    return layers.find((l) => l.id === position.entries[0].layerId) || null;
  }, [position, layers]);

  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.position.copy(camera.position);
    }
  });

  // Determine which images to load initially (small if available, otherwise large/original)
  const initialUrls = useMemo(() => {
    if (!layer) return [];
    return position.entries.map((image) => {
      const filename = pickImageFile(image, 'small');
      const httpsUrl = `${getResourcesEndpoint()}/${layer.root}/${encodeURIComponent(filename)}`;
      return serveOffline ? toOfflineUrl(httpsUrl) : httpsUrl;
    });
  }, [position.entries, layer, serveOffline]);

  // URLs for large images (only if we have compressed/blurred variants)
  const largeUrls = useMemo(() => {
    if (!layer) return [];
    return position.entries.map((image) => {
      if (
        (image.hasCompressedVariants && image.smallFilename) ||
        (image.hasBlurredVariants && image.smallBlurredFilename)
      ) {
        const filename = pickImageFile(image, 'large');
        const httpsUrl = `${getResourcesEndpoint()}/${layer.root}/${encodeURIComponent(filename)}`;
        return serveOffline ? toOfflineUrl(httpsUrl) : httpsUrl;
      }
      return null; // No upgrade needed
    });
  }, [position.entries, layer, serveOffline]);

  const textures = useLoader(CredentialsTextureLoader, initialUrls);

  const groupRef = useRef<THREE.Group | null>(null);
  const [upgradedTextures, setUpgradedTextures] = useState<(THREE.Texture | null)[]>([]);

  // Track which position the upgraded textures belong to so we never
  // composite stale large textures over freshly-loaded small ones.
  const upgradedForPositionRef = useRef<string | null>(null);

  // Reset upgraded textures when position changes to avoid showing stale images
  useEffect(() => {
    setUpgradedTextures([]);
    upgradedForPositionRef.current = null;
  }, [position.entries[0].id]);

  // Load large images in background after initial render
  useEffect(() => {
    const needsUpgrade = largeUrls.some((url) => url !== null);
    if (!needsUpgrade) return;

    const currentPositionId = position.entries[0].id;

    const loadLargeTextures = async () => {
      const loader = new CredentialsTextureLoader();
      const loaded: (THREE.Texture | null)[] = [];

      for (let i = 0; i < largeUrls.length; i++) {
        const url = largeUrls[i];
        if (!url) {
          loaded.push(null);
          continue;
        }

        try {
          const texture = await new Promise<THREE.Texture>((resolve, reject) => {
            loader.load(url, resolve, undefined, reject);
          });
          loaded.push(texture);
        } catch {
          loaded.push(null);
        }
      }

      // Only apply if we're still on the same position — prevents stale
      // large textures from overwriting a freshly-switched image.
      if (upgradedForPositionRef.current === null || currentPositionId === position.entries[0].id) {
        upgradedForPositionRef.current = currentPositionId;
        setUpgradedTextures(loaded);
      }
    };

    loadLargeTextures();
  }, [largeUrls, gl, position.entries]);

  // Use upgraded texture if available, otherwise use initial texture.
  // Guard: only composite upgraded textures when they belong to the
  // current position — prevents the 1-frame flash of the old image
  // that happens because the reset effect (`setUpgradedTextures([])`)
  // is batched and hasn't flushed yet on the first render after a switch.
  const currentPositionId = position.entries[0].id;
  const activeTextures = useMemo(() => {
    const upgradesAreForCurrentPosition = upgradedForPositionRef.current === currentPositionId;
    return textures.map((tex, i) => (upgradesAreForCurrentPosition && upgradedTextures[i] ? upgradedTextures[i] : tex));
  }, [textures, upgradedTextures, currentPositionId]);

  useEffect(() => {
    if (!position.entries[0].focalLengthX) {
      const maxAnisotropy = gl.capabilities.getMaxAnisotropy();
      activeTextures.forEach((texture) => applyEquirectangularTextureSettings(texture, maxAnisotropy));
    }
  }, [activeTextures, gl, position.entries]);

  const sphereGeom = useMemo(() => {
    const sphereGeom = new THREE.SphereGeometry(10, 64, 32);
    sphereGeom.rotateX(Math.PI / 2); // Align sphere UV poles with Z-up
    return sphereGeom;
  }, [textures]);

  const currentSettings = layerSettings[position.entries[0]?.layerId];
  const hasFilters =
    currentSettings &&
    ((currentSettings.imageBrightness ?? 0) !== 0 ||
      (currentSettings.imageContrast ?? 0) !== 0 ||
      (currentSettings.imageSaturation ?? 0) !== 0 ||
      (currentSettings.imageOpacity ?? 1) !== 1);

  return (
    <group ref={groupRef}>
      {position.entries.map((entry, i: number) => {
        if (!entry.focalLengthX) {
          return (
            <EquirectEntry
              key={entry.id}
              entry={entry}
              texture={activeTextures[i]}
              sphereGeom={sphereGeom}
              settings={currentSettings}
            />
          );
        }

        return (
          <PinholeEntry
            key={entry.id}
            entry={entry}
            texture={activeTextures[i]}
            hasFilters={!!hasFilters}
            settings={currentSettings}
          />
        );
      })}
    </group>
  );
};
